/**
 * The **inventory** permission set (`11-inventory.md` § 2).
 *
 * `core_employees` carries two arrays, `permissions` and `psi_permissions`
 * *(verified)*, and the separation is the point: a cashier with full POS
 * rights has no inventory rights, and a stock controller needs no till access.
 * Folding the two together would give every waiter the ability to approve a
 * purchase the moment somebody granted them "write".
 *
 * The source's own PSI keys were never captured, so these are ours. They follow
 * the same `resource:action` scheme as the POS set, prefixed `psi:` so that a
 * key in the wrong array is obvious at a glance rather than plausible.
 *
 * Approval is its own action throughout. Every document in this module has a
 * two-actor workflow — one person raises it, another accepts it — and a set
 * where `write` implied `approve` would quietly collapse that into one.
 */

type PsiPermissionDefinition = {
  readonly key: string;
  readonly name: string;
};

const DEFINITIONS = [
  // Reference data
  { key: "psi:warehouse:read", name: "View warehouses" },
  { key: "psi:warehouse:write", name: "Create and edit warehouses" },
  /**
   * `psi:supply:*` is the stock catalogue — the things a store buys and holds.
   * `psi:supplier:*` below is who it buys them from. Two characters apart and
   * two different grants, so read the name rather than the key when building a
   * role: giving a stock controller `psi:supplier:write` by eye leaves them
   * unable to open the catalogue they were hired for, and nothing says so.
   */
  { key: "psi:supply:read", name: "View supplies, units, categories and brands" },
  { key: "psi:supply:write", name: "Create and edit supplies, units, categories and brands" },
  { key: "psi:supplier:read", name: "View suppliers" },
  { key: "psi:supplier:write", name: "Create and edit suppliers" },

  // Documents
  { key: "psi:purchase:read", name: "View purchase orders" },
  { key: "psi:purchase:write", name: "Raise and edit purchase orders" },
  { key: "psi:purchase:approve", name: "Approve or void a purchase order" },
  { key: "psi:inbound:read", name: "View inbound tickets" },
  { key: "psi:inbound:write", name: "Raise and edit inbound tickets" },
  { key: "psi:inbound:approve", name: "Approve or reject an inbound ticket" },
  { key: "psi:outbound:read", name: "View outbound orders" },
  { key: "psi:outbound:write", name: "Raise and edit outbound orders" },
  { key: "psi:outbound:approve", name: "Approve an outbound order" },
  { key: "psi:count:read", name: "View stock counts" },
  { key: "psi:count:write", name: "Run a stock count" },

  /**
   * Adjustment is the one place stock changes with no commercial document
   * behind it (`11-inventory.md` § 5.5), so it is never implied by counting.
   */
  { key: "psi:count:adjust", name: "Post a stock adjustment" },

  // Stock itself, and what consumes it
  { key: "psi:stock:read", name: "View stock balances and batches" },
  { key: "psi:recipe:write", name: "Link menu items to supplies" },
  { key: "psi:requisition:approve", name: "Review a substore's requisition" },
] as const satisfies readonly PsiPermissionDefinition[];

export type PsiPermissionKey = (typeof DEFINITIONS)[number]["key"];

export const PSI_PERMISSION_CATALOG: readonly PsiPermissionDefinition[] = DEFINITIONS;

export const PSI_PERMISSION_KEYS = DEFINITIONS.map((p) => p.key) as unknown as readonly [
  PsiPermissionKey,
  ...PsiPermissionKey[],
];

const BY_KEY = new Map<string, PsiPermissionDefinition>(DEFINITIONS.map((p) => [p.key, p]));

export function isPsiPermission(value: string): value is PsiPermissionKey {
  return BY_KEY.has(value);
}

export function psiPermission(key: PsiPermissionKey): PsiPermissionDefinition {
  const found = BY_KEY.get(key);
  if (!found) throw new Error(`Unknown inventory permission: ${key}`);
  return found;
}

/**
 * Grantable before a route enforces them. Shrink-only, same contract as the
 * other reserves: an entry leaves the moment a route reads it.
 */
export const RESERVED_PSI_PERMISSIONS: readonly PsiPermissionKey[] = [];
